import { FC, FormEvent, useCallback } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import Button from "@/components/Button";
import { FormLayout, Input, Label, Larger } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import {
  AddButtonHolder,
  BatchBox,
  BatchBoxHeading,
  RemoveButton,
} from "@/components/Testing";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { handleFormErrors, removeExtension } from "@/utils/helpers";
import VideoField from "@/components/Forms/Fields/VideoField";
import TestingPropertiesFields from "./TestingPropertiesFields";
import { RootState } from "@/utils/store";
import { selectTeamTestingProperties } from "@/selectors";
import CurrencyField from "@/components/Forms/CurrencyField";
import { NewDropdown } from "@/components/Forms/Dropdown";
import { OptimisationProjectNetworkEnum } from "@/utils/enums";
import ImageField from "@/components/Forms/ImageField";
import TestingAudienceField from "./TestingAudienceField";
import FormField from "@/components/Forms/FormField";
import TestingProjectName from "./TestingProjectName";
import AuthorField from "./AuthorField";
import useOnCloseWithConfirmation from "@/hooks/useOnCloseWithConfirmation";
import { getAudienceSegments } from "@/utils/optimisationTests";
import { campaignAutomationsOptions } from "@/utils/data/testing";
import CompetitionField from "./CompetitionField";

export type FormDataT = {
  video: string;
  title: string;
  max_cpv: string;
  campaign_budget: string;
  batch: number;
  author: string;
  campaign_start_date: string;
  campaign_end_date: string;
  testing_audience_id?: number;
  audience_segment: string;
  campaign_automations: string;
  c_batch: number;
  multitest_enabled: boolean;
  variations: {
    name: string;
    image: string;
    file: File | null;
  }[];
};

interface Props {
  onClose: () => void;
  network: OptimisationProjectNetworkEnum;
  projectTitle: string;
  competition?: {
    id: number;
    ended_at?: string;
  };
}

const ThumbsAndTitlesFormModal: FC<Props> = ({
  onClose,
  network,
  projectTitle,
  competition,
}) => {
  const { id, teamId } = useParams();

  const { testingSettings, eytvTestingAudience } = useSelector(
    (state: RootState) => selectTeamTestingProperties(state, teamId!)
  );

  const audienceSegments = getAudienceSegments(
    testingSettings?.google_ads_client_id ?? ""
  );

  const userName = useSelector(
    (state: RootState) => state.auth.user?.name ?? ""
  );

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const methods = useForm<FormDataT>({
    defaultValues: {
      max_cpv: testingSettings?.thumbs_and_titles_max_cpv?.toString() ?? "",
      campaign_budget:
        testingSettings?.thumbs_campaign_budget?.toString() ?? "",
      video: "",
      title: "",
      batch: 1,
      author: userName,
      campaign_start_date: DateTime.now().toISODate(),
      campaign_end_date: DateTime.now().plus({ days: 1 }).toISODate(),
      variations: [{ name: "", file: null, image: "" }],
      audience_segment: "",
      multitest_enabled: false,
      campaign_automations: campaignAutomationsOptions[0].value,
      testing_audience_id: eytvTestingAudience ?? 0,
      c_batch: 0,
    },
  });

  const {
    register,
    control,
    handleSubmit,
    clearErrors,
    setError,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = methods;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "variations",
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(
      async ({
        video,
        title,
        variations,
        max_cpv,
        campaign_budget,
        campaign_start_date,
        campaign_end_date,
        testing_audience_id,
        batch,
        author,
        audience_segment,
        campaign_automations,
        c_batch,
        multitest_enabled,
      }) => {
        try {
          const body: object = {
            optimisation_test_id: id!,
            video,
            batch,
            title,
            network,
            multitest_enabled,
            campaign_start_date,
            ...(!!testing_audience_id && { testing_audience_id }),
            campaign_end_date,
            author,
            audience_segment,
            campaign_automations,
            c_batch,
            variations: variations.map(({ image, name }) => ({ image, name })),
            ...(network === OptimisationProjectNetworkEnum.YOUTUBE && {
              max_cpv,
              campaign_budget,
            }),
          };

          const { error } = await api.post<typeof body, { error?: string }>(
            "/v1/optimisation-test-units/thumbs-and-titles",
            body
          );

          if (error) {
            return alerts.error(error);
          }

          queryClient.invalidateQueries(["optimisation_tests"], {
            exact: false,
          });

          alerts.success(
            `Thumbs and Titles tests have been successfully created`
          );

          onClose();
        } catch (err) {
          handleFormErrors<FormDataT>(err, setError, alerts, true, true);
        }
      }
    )(e);
  };

  const onThumbnailsUploaded = useCallback(
    (images: Array<{ path?: string; file: File }>, fieldsIndex?: number) => {
      images
        .filter(({ path }) => !!path)
        .forEach(({ path, file }, index) => {
          if (index === 0) {
            return update(fieldsIndex ?? 0, {
              name: removeExtension(file.name),
              image: path!,
              file,
            });
          }
          append({
            name: removeExtension(file.name),
            image: path!,
            file,
          });
        });
      if (images.filter(({ path }) => !path).length) {
        alerts.error(
          "Failed to upload some images",
          "Those images will be excluded from variations"
        );
      }
    },
    [update, append, alerts]
  );

  return (
    <Modal>
      <SidePanel heading={`Upload thumbs tests`} onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <FormProvider {...methods}>
            <SidePanelContent>
              <FormLayout>
                <Larger>
                  <TestingProjectName projectTitle={projectTitle} />
                </Larger>
                {/* TODO: uncomment when ready */}
                {/* <Larger>
                  <SelectionBox>
                    <SelectionHeading $type="smallBold">
                      Thumbs & titles type
                    </SelectionHeading>
                    <Text $type="smallMedium" $color="gray">
                      Run as a batch or generate variations automatically.
                    </Text>
                    <ControlsHolder>
                      <Controls>
                        <ControlsButton type="button" active={true}>
                          Batch
                        </ControlsButton>
                        <ControlsButton type="button" active={false} disabled>
                          Auto-generated
                        </ControlsButton>
                      </Controls>
                    </ControlsHolder>
                  </SelectionBox>
                </Larger> */}
                {network === OptimisationProjectNetworkEnum.YOUTUBE && (
                  <>
                    <Larger>
                      <TestingPropertiesFields
                        hasErrors={!!errors.max_cpv || !!errors.campaign_budget}
                        title="Thumbs testing properties"
                      >
                        <Larger>
                          <Label htmlFor="thumbsMaxCpv">
                            Thumbs Maximum CPV
                          </Label>
                          <CurrencyField<FormDataT>
                            control={control}
                            name="max_cpv"
                            inputId="thumbsMaxCpv"
                            placeholder="Add value"
                          />
                          {!!errors.max_cpv && (
                            <ValidationMessage>
                              {errors.max_cpv.message}
                            </ValidationMessage>
                          )}
                        </Larger>
                        <Larger>
                          <Label htmlFor="campaignBudgetInput">
                            Thumbs Campaign Budget
                          </Label>
                          <CurrencyField<FormDataT>
                            control={control}
                            name="campaign_budget"
                            inputId="campaignBudgetInput"
                            placeholder="Add value"
                          />
                          {!!errors.campaign_budget && (
                            <ValidationMessage>
                              {errors.campaign_budget.message}
                            </ValidationMessage>
                          )}
                        </Larger>
                      </TestingPropertiesFields>
                    </Larger>
                    <FormField<FormDataT>
                      control={control}
                      id="mutlitestSwitch"
                      name="multitest_enabled"
                      type="switch"
                      headline="Multitest"
                      description="Each video and thumbnail can be tested up to three times. Outcome will show individual results of each video as well as their average, which will provide a more accurate overall result. However, this method can cost up to three times more."
                    />
                    <TestingAudienceField<FormDataT>
                      control={control}
                      error={errors.testing_audience_id}
                    />
                    <FormField<FormDataT, string>
                      control={control}
                      label="Audience Segment"
                      type="select"
                      name="audience_segment"
                      options={audienceSegments}
                    />
                  </>
                )}
                <Larger>
                  <VideoField<FormDataT>
                    name="video"
                    control={control}
                    label="Video File"
                  />
                </Larger>
                <Larger>
                  <Label htmlFor="titleInput">Title</Label>
                  <Input
                    id="titleInput"
                    placeholder="Add title"
                    type="text"
                    $hasError={!!errors.title}
                    {...register("title")}
                  />
                  {!!errors.title && (
                    <ValidationMessage>
                      {errors.title.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="batchSelect">Batch</Label>
                  <NewDropdown<number>
                    options={Array.from({ length: 11 }, (_, i) => ({
                      value: i,
                      label: `T${i}`,
                    }))}
                    control={control}
                    name="batch"
                    id="batchSelect"
                  />
                  {!!errors.batch && (
                    <ValidationMessage>
                      {errors.batch.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <AuthorField<FormDataT> control={control} name="author" />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign Start Date"
                  type="datepicker"
                  name="campaign_start_date"
                  datepickerType="date"
                />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign End Date"
                  type="datepicker"
                  name="campaign_end_date"
                  datepickerType="date"
                />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign Automations"
                  type="select"
                  options={campaignAutomationsOptions}
                  name="campaign_automations"
                />
              </FormLayout>
              {fields.map((item, index) => (
                <BatchBox key={item.id}>
                  <BatchBoxHeading $type="button">
                    Batch variation #{index + 1}
                    {index > 0 && (
                      <RemoveButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(index);
                        }}
                      >
                        Remove
                      </RemoveButton>
                    )}
                  </BatchBoxHeading>
                  <FormLayout>
                    <Larger>
                      <Label htmlFor={`variationNameInput-${index}`}>
                        Variation Name
                      </Label>
                      <Input
                        id={`variationNameInput-${index}`}
                        placeholder="Add name"
                        type="text"
                        $hasError={!!errors.variations?.[index]?.name}
                        {...register(`variations.${index}.name`)}
                      />
                      {!!errors.variations?.[index]?.name && (
                        <ValidationMessage>
                          {errors.variations?.[index]?.name?.message}
                        </ValidationMessage>
                      )}
                    </Larger>
                    <Larger>
                      <ImageField
                        fileName={`variations.${index}.file`}
                        pathName={`variations.${index}.file`}
                        multiple
                        notes="JPG, PNG, GIF. Size: 1280 x 720 / Up to 2MB</br>Upload multiple files to create multiple variations"
                        fieldsIndex={index}
                        id={`variations-${index}-image-field`}
                        onUploaded={onThumbnailsUploaded}
                      />
                      {!!errors.variations?.[index]?.image && (
                        <ValidationMessage>
                          {errors.variations?.[index]?.image?.message}
                        </ValidationMessage>
                      )}
                    </Larger>
                  </FormLayout>
                </BatchBox>
              ))}
              <AddButtonHolder>
                <Button
                  type="button"
                  $type="colorized"
                  onClick={() =>
                    append({
                      name: "",
                      file: null,
                      image: "",
                    })
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 2V14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 8H14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add variation
                </Button>
              </AddButtonHolder>
              {!!errors.variations && (
                <ValidationMessage>
                  {errors.variations.message}
                </ValidationMessage>
              )}
              {!competition?.ended_at && (
                <CompetitionField control={control} setValue={setValue} />
              )}
            </SidePanelContent>
          </FormProvider>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Upload
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ThumbsAndTitlesFormModal;
